// The dispatcher's saved dispatch plans, newest plan date first, optionally
// within plan dates (from = to for one day). Opening one shows it in the
// dispatch planner, so a plan can be picked up again without finding the run it
// came from.

import { useEffect, useState } from "react";

import { listPlans } from "../api/client";
import { clockOnDay, todayIn } from "../lib/format";
import { useT } from "../lib/i18n";
import type { PlanSummary } from "../types";

export function SavedPlansView({ onOpenPlan }: { onOpenPlan: (planId: string) => void }) {
  const t = useT();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [plans, setPlans] = useState<PlanSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    listPlans({ ...(dateFrom && { dateFrom }), ...(dateTo && { dateTo }) })
      .then((p) => live && setPlans(p))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [dateFrom, dateTo]);

  return (
    <section className="starter" style={{ maxWidth: 1100 }}>
      <div className="starter-card" style={{ maxWidth: "none" }}>
        <h1>{t("plans.title")}</h1>
        <p className="starter-desc">{t("plans.desc")}</p>

        <div className="config-row" style={{ alignItems: "flex-end" }}>
          <label className="control">
            <span className="control-label">{t("plans.dateFrom")}</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label className="control">
            <span className="control-label">{t("plans.dateTo")}</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
        </div>

        {error && (
          <p role="alert" className="upload-status error">
            {t("plans.loadFailed")} {error}
          </p>
        )}
        {plans?.length === 0 && <p className="plan-muted">{t("plans.empty")}</p>}
        {plans && plans.length > 0 && (
          <table className="manifest" aria-label={t("plans.table")}>
            <thead>
              <tr>
                <th>{t("plans.col.date")}</th>
                <th>{t("plans.col.warehouse")}</th>
                <th>{t("plans.col.status")}</th>
                <th>{t("plans.col.size")}</th>
                <th>{t("plans.col.saved")}</th>
                <th>{t("plans.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.plan_date}
                    {p.origin === "hand" && (
                      <span className="plan-chip" data-testid="plan-origin">
                        {t("plan.originHand")}
                      </span>
                    )}
                  </td>
                  <td>{p.depot_label}</td>
                  <td>{t(`plan.phase.${p.status}`)}</td>
                  <td>
                    {t("plans.trucks", { n: p.trucks })} · {t("plans.orders", { n: p.orders })}
                    {p.unrouted > 0 && (
                      <div className="plan-muted">{t("plans.unrouted", { n: p.unrouted })}</div>
                    )}
                    {p.threepl_unassigned > 0 && (
                      <div className="plan-muted">
                        {t("plans.threeplUnassigned", { n: p.threepl_unassigned })}
                      </div>
                    )}
                  </td>
                  <td>
                    {clockOnDay(p.updated_at, p.timezone, todayIn(p.timezone))} · v{p.version}
                  </td>
                  <td>
                    <button
                      className="btn"
                      aria-label={t("plans.openFor", { date: p.plan_date })}
                      onClick={() => onOpenPlan(p.id)}
                    >
                      {t("plans.open")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
